import { Injectable, computed, inject, linkedSignal } from '@angular/core';
import { ExtRouteService, SnackbarService } from '@comparit/core';
import { KundeTokenService } from '@comparit/infrastructure';
import { SaveOrigin } from '@comparit/common-api';
import { ResultsService } from '../../../services/results.service';
import { StepInitService } from '../../../services/step-init.service';
import type { ZwischenstandKontakt } from '../../components/zwischenstand-speichern-modal/zwischenstand-speichern-modal.component';
import { StandSpeichernService } from '../../components/zwischenstand-speichern-modal/zwischenstand-speichern.service';
import { KfzRoutes } from '../../app.routes.paths';
import { MlpCallbackContext, buildMlpCallbackUrl } from './mlp-callback.helper';
import type { MlpCampaignPrefill } from './mlp-campaign-url.helper';

// KFZ-2547 asks for the link "from page 9 (SF-Klassen & Vorversicherung)". Pages are numbered
// from the start page, which the navigator does not count: it renders `Schritt ${step - 1}`
// (StepInitService), so the page the ticket calls 9 is step 10.
const CALLBACK_FROM_STEP = 10;

type RueckrufKontakt = Partial<ZwischenstandKontakt>;

@Injectable({ providedIn: 'root' })
export class MlpCallbackContextService {
  private readonly stepInit = inject(StepInitService);
  private readonly extRoute = inject(ExtRouteService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly snackbar = inject(SnackbarService);
  private readonly standSpeichern = inject(StandSpeichernService);
  private readonly results = inject(ResultsService);
  private blockiertHinweis?: number;

  // Latches on, so the link stays visible when the customer navigates back.
  private readonly reachedCallbackStep = linkedSignal({
    source: this.stepInit.currentStep,
    computation: (step, previous) => previous?.value || step >= CALLBACK_FROM_STEP,
  });

  readonly context = computed<MlpCallbackContext | null>(() => {
    const url = this.extRoute.url();
    if (
      url?.startsWith(KfzRoutes.rechner) ||
      url?.startsWith(KfzRoutes.vergleich) ||
      url?.startsWith(KfzRoutes.angebot)
    ) {
      return 'entscheidung';
    }
    return this.reachedCallbackStep() ? 'fragebogen' : null;
  });

  open(): void {
    const context = this.context();
    if (!context) {
      return;
    }

    const origin = this.saveOrigin();
    if (!origin) {
      this.openBooking(context, this.loginKontakt() ?? {});
      return;
    }

    const berechnungId =
      origin === SaveOrigin.Ergebnisse || origin === SaveOrigin.Vergleich
        ? this.results.berechnungResponse()?.berechnungId
        : undefined;
    this.standSpeichern.openWithCurrentState(
      origin,
      berechnungId,
      this.tarifdetailsVorgangId() ?? undefined,
      kontakt => this.openBooking(context, kontakt)
    );
  }

  loginRequired(): boolean {
    const origin = this.saveOrigin();
    return (
      !!origin &&
      origin !== SaveOrigin.Grunddaten &&
      !this.kundeToken.getToken() &&
      !this.kundeToken.staffMode()
    );
  }

  reset(): void {
    this.reachedCallbackStep.set(false);
  }

  // The same origin each page's own "Stand speichern" sends; pages after the Antrag save nothing.
  private saveOrigin(): SaveOrigin | null {
    const url = this.extRoute.url() ?? '';
    if (url.startsWith(KfzRoutes.formular)) {
      return SaveOrigin.Grunddaten;
    }
    if (url.startsWith(KfzRoutes.rechner)) {
      return SaveOrigin.Ergebnisse;
    }
    if (url.startsWith(KfzRoutes.vergleich) || url.startsWith(KfzRoutes.angebot)) {
      return SaveOrigin.Vergleich;
    }
    if (this.tarifdetailsVorgangId()) {
      return SaveOrigin.Antrag;
    }
    return null;
  }

  private tarifdetailsVorgangId(): string | null {
    const prefix = KfzRoutes.tarifdetails.replace(':vorgangId', '');
    const url = this.extRoute.url() ?? '';
    return url.startsWith(prefix) ? url.slice(prefix.length).split(/[/?#]/)[0] || null : null;
  }

  private openBooking(context: MlpCallbackContext, kontakt: RueckrufKontakt): void {
    // Built on demand: the checksum in it is only accepted a day either side.
    const url = () => buildMlpCallbackUrl(context, this.prefill(kontakt));
    // No 'noopener': it makes open() return null, which is also how a blocked popup shows.
    const tab = window.open(url(), '_blank');
    if (tab) {
      tab.opener = null;
      return;
    }

    this.snackbar.close(this.blockiertHinweis);
    this.blockiertHinweis = this.snackbar.warning(
      'Die Rückrufseite wurde von deinem Browser blockiert.',
      {
        disableTimeout: true,
        action: {
          label: 'Rückruf vereinbaren',
          callback: () => {
            this.snackbar.close(this.blockiertHinweis);
            window.open(url(), '_blank', 'noopener');
          },
          value: null,
        },
      }
    );
  }

  private prefill(kontakt: RueckrufKontakt): MlpCampaignPrefill {
    return {
      vorname: kontakt.vorname,
      nachname: kontakt.nachname,
      Kdnr: this.kundeToken.mlpKundennummer(),
      kontaktweg: kontakt.email,
    };
  }

  private loginKontakt(): RueckrufKontakt | null {
    if (!this.kundeToken.getToken()) {
      return null;
    }
    return {
      vorname: this.kundeToken.vorname() ?? undefined,
      nachname: this.kundeToken.nachname() ?? undefined,
      email: this.kundeToken.email() ?? undefined,
    };
  }
}
